import React, { useEffect, useState } from 'react';
import { NvTab, NvResponsiveTabsDisplayType } from 'shared/components/nv-responsive-tabs';
import { NvResponsiveTabsRow } from 'shared/components/nv-responsive-tabs-row';
import { createGridStyles, halfSpacing, quarterSpacing } from 'styles/global_defaults/scaffolding';
import t from 'react-translate';
import { textSmallFontSize } from 'styles/global_defaults/fonts';
import { AngularServicesContext } from 'react-app';
import { AnalyticsDashBoardType } from 'redux/schemas/app/analytics';
import { BrowserRouter, useHistory, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { gray6, warning } from 'styles/global_defaults/colors';
import { css } from '@emotion/react';
import { getLecturePageByQuestionId } from 'redux/selectors/course';
import AnalyticsDashboard from './analytics-dashboard';
import PracticeAnalyticsTab from './practice-analytics-tab';
import QuizAndSurvey from './quiz-survey-analytics';

const styles = css`
  .tab-container {
    position: sticky;
    top: 0;
    z-index: 2;
    border-bottom: solid 1px ${gray6};
  }
  .quiz-survey {
    .bottom-border {
      position: relative;
      left: 0;
      top: ${halfSpacing}px;
      width: 100%;
      height: ${quarterSpacing}px;
      background-color: ${warning};
    }
  }
  .quiz-survey-dashboard {
    max-width: 1030px;
  }
  .bs4-dropdown {
    .bs4-dropdown-menu {
      min-width: 200px !important;
    }
    .accordion-collapse {
      overflow-y: scroll;
    }
    .expanded {
      max-height: 300px !important;
    }
  }
  .practice-dropdown-title {
    align-items: center;
    display: flex;
    margin-left: 25px;

    .bs4-dropdown-menu {
      &.show {
        transform: translate3d(0%, 35px, 0px) !important;
        left: 20px !important;
      }
    }
  }
`;

enum TABS {
  OVERVIEW = 0,
  ACTIVITIES = 1,
  SKILLS = 2,
  COHORTS = 3,
  QUIZ_SURVEY = 4,
  PRACTICE = 5,
}

const CourseAnalytics = () => {
  const { cohort } = useSelector((state) => state.app.qrveyConfig);
  const showCohorts = cohort;
  const skillTags = Object.values(useSelector((state) => state.models.institutions[state.app.currentInstitutionId].skillTags));
  const institutionHasSkilltags = skillTags?.length > 0;
  const history = useHistory();
  const location = useLocation();
  let currentActiveTab = 0;

  const sections = [
    {
      url: '/activities',
      activeId: 1,
      display: true,
    },
    {
      url: '/skills',
      activeId: 2,
      display: institutionHasSkilltags,
    },
    {
      url: '/cohorts',
      activeId: 3,
      display: true,
    },
  ];
  // set the default selected tab based on the url.
  sections.forEach((section) => {
    if (location?.hash?.endsWith(section.url) && section.display && !questionSetId) {
      currentActiveTab = section.activeId;
    }
  });

  const [activeTab, setActiveTab] = useState(currentActiveTab);
  const [prevTab, setPrevTab] = useState<number>(currentActiveTab);
  const [selectedPage, setSelectedPage] = useState<string>(null);
  const [questionSetId, setQuestionSetId] = useState(null);
  const { $state } = React.useContext(AngularServicesContext);
  const { catalogId } = $state?.params;
  const baseURL = `/#!/courses/${catalogId}/analytics`;
  const lecturePageId = useSelector(state => getLecturePageByQuestionId(state, { id: questionSetId }));
  const selectTab = (id) => {
    setActiveTab(id);
    setQuestionSetId(null);
    // changing history so reloading will mark the right tab
    if (id === 0) {
      history.push(baseURL);
    }
    if (id === 1) {
      history.push(`${baseURL}/activities`);
    }
    if (id === 2 && institutionHasSkilltags) {
      history.push(`${baseURL}/skills`);
    }
    if (id === 3) {
      history.push(`${baseURL}/cohorts`);
    }
  };

  const tabs: NvTab[] = [
    {
      text: t.COURSE_ANALYTICS.OVERVIEW(),
      onClick: () => selectTab(0),
    }, {
      text: t.COURSE_ANALYTICS.ACTIVITIES(),
      onClick: () => selectTab(1),
    },
  ];
  if (institutionHasSkilltags) {
    tabs.push({
      text: t.COURSE_ANALYTICS.SKILLS(),
      onClick: () => selectTab(2),
    });
  }
  if (showCohorts) {
    tabs.push({
      text: t.COURSE_ANALYTICS.COHORTS(),
      onClick: () => selectTab(3),
    });
  }

  const tabStyles = {
    ...createGridStyles(1, 2),
    fontSize: `${textSmallFontSize}px`,
  };
  const onSelectQuestion = (id: number) => {
    setQuestionSetId(id);
    setActiveTab(4);
    history.push(`${baseURL}/quiz-and-survey/${id}`);
  };

  // This useEffect manages the opening and closing of the Practice Room Modal.
  // When the user opens the Practice Room Modal, we create a new instance of QRvey.
  // Since QRvey does not support handling multiple instances simultaneously, we ensure
  // that any existing QRvey instance is closed before opening a new one.
  useEffect(() => {
    // Check if the Practice Room Modal is being opened
    if ($state?.current?.name === 'practice-room-modal') {
      // Update the selected page to 'practice-room-modal'
      setSelectedPage($state?.current?.name);

      // Store the current active tab before switching
      setPrevTab(activeTab);

      // Reset question set and activate the tab for the practice room
      setQuestionSetId(null);
      setActiveTab(TABS.PRACTICE); // Assuming 5 is the tab for the practice room
    } else if (selectedPage === 'practice-room-modal') {
      // If the Practice Room Modal is being closed, restore the previous state
      setPrevTab(TABS.OVERVIEW); // Reset the previous tab
      setSelectedPage(null);
      setActiveTab(prevTab); // Restore the active tab to the previous state
    }
  }, [$state?.current?.name]); // Added dependencies for more accurate tracking

  useEffect(() => {
    if ($state.params?.questionId) {
      setQuestionSetId($state.params?.questionId);
      setActiveTab(4);
    }
  }, [$state.params?.questionId]);

  return (
    <div css={styles}>
      <div className='d-flex align-items-center pr-2 bg-white tab-container'>
        <NvResponsiveTabsRow
          style={tabStyles}
          defaultTabs={tabs}
          tabType={NvResponsiveTabsDisplayType.TEXT_ONLY}
          tabTextClass='card-title'
          selectedTabIndex={activeTab}
          revertActiveTab={questionSetId ? -1 : activeTab}
        />
        <PracticeAnalyticsTab catalogId={catalogId} />
        <div className='quiz-survey ml-4'>
          <QuizAndSurvey
            onSelect={onSelectQuestion}
            selectedId={questionSetId}
          />
          {questionSetId && (
            <div className='bottom-border' />
          )}
        </div>
      </div>

      {/* This to conditionals are neccesary to rerender the qrvey instance on click of the tabs */}
      {activeTab === 0
      && <AnalyticsDashboard dashboardType={AnalyticsDashBoardType.COURSE} configKeyName='config_overview' pageIdKey='overview' /> }
      {activeTab === 1
      && <AnalyticsDashboard dashboardType={AnalyticsDashBoardType.COURSE} configKeyName='config_activities' pageIdKey='activities' />}
      {activeTab === 2
      && <AnalyticsDashboard dashboardType={AnalyticsDashBoardType.COURSE} configKeyName='config_skills' pageIdKey='skills' />}
      {activeTab === 3
      && <AnalyticsDashboard dashboardType={AnalyticsDashBoardType.COHORTS} configKeyName='config_cohorts' pageIdKey='cohorts' />}
      {questionSetId
       && (
       <div className='d-flex justify-content-center quiz-survey-dashboard mx-auto'>
         <AnalyticsDashboard
           dashboardType={AnalyticsDashBoardType.QUESTION_SET}
           configKeyName={`config_question_set_${questionSetId}`}
           pageIdKey='question_set'
           questionSetId={questionSetId}
           lecturePageId={lecturePageId}
         />
       </div>
       )}
    </div>

  );
};
export default CourseAnalytics;

export const CourseAnalyticsRoute = () => <BrowserRouter forceRefresh><CourseAnalytics /></BrowserRouter>;
